import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

import { useTranslation } from 'react-i18next';
import { setError } from '../../../actions';
import AdLabelPlaceholder from '../../Placeholders/AdLabelPlaceholder';
import AdGalleryPlaceholder from '../../Placeholders/AdGalleryPlaceholder';
import { useFullPhoneNumbers } from '../../../hooks/useFullPhoneNumbers';
import { getSelectedAgencyQuery } from '../../../api/queries';
import { AgencyPageSchema } from './AgencyPageSchema';
import { AnalyticLogTargetTypeEnum } from '../../../graphql/types';

const AgencyFilter = dynamic(
  () => import('../../Agency/AgencyFilter').then(mod => mod.AgencyFilter),
  {
    ssr: false
  }
);

const AgencyHeader = dynamic(() => import('../../Agency/AgencyHeader'), {
  ssr: false
});

const ContactsSection = dynamic(
  () => import('../../Agency/Sections/ContactsSection'),
  {
    ssr: false
  }
);

const PhonesSection = dynamic(
  () => import('../../Agency/Sections/PhonesSection'),
  {
    ssr: false
  }
);

const Container = dynamic(() => import('../../Ad/Container'), {
  ssr: false
});

const BottomPhoneButton = dynamic(
  () => import('../../BottomPhoneButton').then(mod => mod.BottomPhoneButton),
  {
    ssr: false
  }
);

const TopCoverImg = dynamic(() => import('../../Ad/TopCoverImg'), {
  ssr: false
});

const DescriptionSection = dynamic(
  () => import('../../Agency/Sections/DescriptoinSection'),
  {
    ssr: false
  }
);

const AgencyPage = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const agencySlug = router.query.id;
  const { loading, data, error } = useQuery(getSelectedAgencyQuery, {
    variables: { slug: agencySlug },
    ssr: false
  });
  const agencyData = data?.agency || {};
  const allCategories = data?.categories || [];
  const isShowFilters = !!agencyData.itemsCount;
  const city = agencyData?.city?.name
    ? t('pages.agency.city', { city: agencyData?.city?.name })
    : '';

  const { onClickHandler, phonesShown, setIsVisible, isVisible } =
    useFullPhoneNumbers({
      id: agencySlug,
      residenceId: null,
      path: 'agentlikler',
      targetType: AnalyticLogTargetTypeEnum.Agency,
      agencyId: agencyData.id
    });

  useEffect(() => {
    if (error) dispatch(setError(true));
  }, [error]);

  if (loading)
    return (
      <>
        <AdLabelPlaceholder />
        <AdGalleryPlaceholder />
      </>
    );

  return (
    <>
      {agencyData && <AgencyPageSchema agencyData={agencyData} />}
      <TopCoverImg imgUrl={agencyData.coverUrl} title={agencyData.name} />
      <Container>
        <AgencyHeader
          apartmentCount={agencyData.itemsCount}
          imageUrl={agencyData.coverUrl}
          title={agencyData.name}
          views={agencyData.viewsCount}
          logoUrl={agencyData.logoUrl}
        />
        <DescriptionSection description={agencyData.description} />
        <ContactsSection
          address={`${city}${agencyData.contactAddress}`}
          lat={agencyData.latitude}
          lng={agencyData.longitude}
          timetable={agencyData.schedule}
        />
        <PhonesSection
          phonesMasked={agencyData.phonesMasked || []}
          clickHandler={onClickHandler}
          {...{ phonesShown }}
        />
        {isShowFilters && (
          <AgencyFilter
            categories={agencyData.itemCategories}
            allCategories={allCategories}
            agencyId={agencyData.id}
            agencySlug={agencySlug}
          />
        )}
      </Container>
      <BottomPhoneButton
        phones={phonesShown}
        clickHandler={onClickHandler}
        agencyPage
        {...{ setIsVisible, isVisible }}
      />
    </>
  );
};

AgencyPage.propTypes = {
  setErrorUI: PropTypes.func
};

export default AgencyPage;
